import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { api } from "../lib/api";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { UserPlus, Trash2, Shield, User, KeyRound, Link2, Copy, Check, X, Ticket, HeartHandshake, Heart, MailCheck, BellRing } from "lucide-react";
import { useAuth } from "../context/AuthContext";

function inviteUrl(token) {
  return `${window.location.origin}/invite/${token}`;
}

export function ManageUsersDialog({ open, onOpenChange, family, onFamilyChanged }) {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [famName, setFamName] = useState("");
  const [invites, setInvites] = useState([]);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [inviteForm, setInviteForm] = useState({ name: "", role: "member", email: "" });
  const [previewEmail, setPreviewEmail] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [reminderCfg, setReminderCfg] = useState({ invite_reminder_days: 3, invite_second_nudge: false, invite_second_nudge_days: 7 });
  const [copied, setCopied] = useState(null);
  const [requests, setRequests] = useState([]);
  const [donations, setDonations] = useState({ donations: [], totals: {} });

  const loadUsers = () => api.listUsers().then((d) => setUsers(d.users || [])).catch(() => {});
  const loadInvites = () => api.listInvites().then((d) => setInvites(d.invites || [])).catch(() => {});
  const loadRequests = () => api.listContribRequests().then((d) => setRequests(d.requests || [])).catch(() => {});
  const loadDonations = () => api.listDonations().then((d) => setDonations(d)).catch(() => {});
  useEffect(() => { if (open) { loadUsers(); loadInvites(); loadRequests(); loadDonations(); loadReminderCfg(); setResetFor(null); setFamName(family?.name || ""); setPreviewEmail((user?.username || "").includes("@") ? user.username : ""); } }, [open]);

  const loadReminderCfg = async () => {
    try {
      const s = await api.getSettings();
      setReminderCfg({
        invite_reminder_days: s?.invite_reminder_days ?? 3,
        invite_second_nudge: !!s?.invite_second_nudge,
        invite_second_nudge_days: s?.invite_second_nudge_days ?? 7,
      });
    } catch (e) { console.error("Failed to load reminder settings", e); }
  };

  const saveReminders = async (patch) => {
    const next = { ...reminderCfg, ...patch };
    setReminderCfg(next);
    try {
      await api.setInviteReminders(next);
      toast.success("Reminder settings saved");
    } catch (e) {
      toast.error("Could not save reminder settings");
    }
  };

  const saveFamName = async () => {
    const n = famName.trim();
    if (!n) return toast.error("Enter a family name");
    try { await api.renameFamily(n); toast.success("Family renamed"); onFamilyChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not rename"); }
  };

  const toggleRole = async (u) => {
    const next = u.role === "admin" ? "member" : "admin";
    try {
      await api.changeUserRole(u.id, next);
      toast.success(next === "admin" ? `${u.name || u.username} is now an admin` : `${u.name || u.username} is no longer an admin`);
      loadUsers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update role"); }
  };

  const approveRequest = async (id) => {
    try { await api.approveContribRequest(id); toast.success("Approved — an invite link is ready below to send them"); loadRequests(); loadInvites(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not approve"); }
  };
  const declineRequest = async (id) => {
    try { await api.declineContribRequest(id); loadRequests(); } catch { toast.error("Could not decline"); }
  };
  const removeRequest = async (id) => {
    try { await api.deleteContribRequest(id); loadRequests(); } catch { toast.error("Could not remove"); }
  };

  const create = async () => {
    if (!form.username.trim() || !form.password) return toast.error("Username and password required");
    setBusy(true);
    try {
      await api.createUser({ ...form, username: form.username.trim() });
      toast.success(`Account "${form.username}" created`);
      setForm({ username: "", password: "", name: "", role: "member" });
      loadUsers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create account");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.deleteUser(id); loadUsers(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); }
  };

  const doReset = async (id) => {
    if (!resetPw || resetPw.length < 4) return toast.error("Enter a password (min 4 chars)");
    try {
      await api.resetUserPassword(id, resetPw);
      toast.success("Password reset");
      setResetFor(null); setResetPw("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
  };

  const sendPreview = async (kind) => {
    const em = previewEmail.trim();
    if (!em.includes("@")) return toast.error("Enter a valid email address");
    setPreviewBusy(true);
    try {
      await api.emailPreview(em, kind, window.location.origin);
      toast.success(`Sample ${kind === "reset" ? "reset" : "welcome"} email sent to ${em}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send sample email");
    } finally { setPreviewBusy(false); }
  };

  const generateInvite = async () => {
    setBusy(true);
    try {
      await api.createInvite({ name: inviteForm.name.trim(), role: inviteForm.role, email: inviteForm.email.trim(), origin: window.location.origin });
      toast.success("Invite link created — copy & share it");
      setInviteForm({ name: "", role: "member", email: "" });
      loadInvites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create invite");
    } finally { setBusy(false); }
  };

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(inviteUrl(token));
      setCopied(token);
      setTimeout(() => setCopied(null), 1800);
      toast.success("Link copied");
    } catch { toast.error("Copy failed — select and copy manually"); }
  };

  const revoke = async (id) => {
    try { await api.revokeInvite(id); loadInvites(); } catch { toast.error("Could not revoke"); }
  };

  const inviteStatus = (inv) => inv.used ? { label: `Used by @${inv.used_username}`, cls: "bg-[#D8E0E8] text-[#64748B]" }
    : inv.expired ? { label: "Expired", cls: "bg-red-50 text-red-500" }
    : { label: "Active", cls: "bg-[#E8EEF3] text-[#082D4A]" };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="manage-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-serif text-2xl text-[#082D4A]"><Shield size={20} /> Family accounts</DialogTitle>
          <DialogDescription>Create accounts, send invite links, and reset passwords for family members.</DialogDescription>
        </DialogHeader>

        {/* Rename family */}
        <div className="rounded-xl border border-[#082D4A]/20 bg-[#E8EEF3]/60 p-3" data-testid="rename-family-panel">
          <Label className="text-xs">Family name (shown at the top)</Label>
          <div className="mt-1 flex gap-2">
            <Input className="h-9 bg-white" data-testid="family-name-input" value={famName} onChange={(e) => setFamName(e.target.value)} placeholder="e.g. The Walters Family" />
            <Button onClick={saveFamName} data-testid="save-family-name" className="h-9 shrink-0 bg-[#082D4A] text-white hover:bg-[#08223A]">Save</Button>
          </div>
        </div>

        {/* Create account */}
        <div className="grid grid-cols-2 gap-2 rounded-xl border border-[#D8E0E8] bg-[#F4F6F8] p-3">
          <div className="space-y-1"><Label className="text-xs">Username</Label><Input className="h-8 bg-white" data-testid="new-user-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Display name</Label><Input className="h-8 bg-white" data-testid="new-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="space-y-1"><Label className="text-xs">Password</Label><Input className="h-8 bg-white" type="text" data-testid="new-user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
          <div className="space-y-1">
            <Label className="text-xs">Role</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger className="h-8 bg-white" data-testid="new-user-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="member">Member</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
            </Select>
          </div>
          <Button onClick={create} disabled={busy} data-testid="create-user-button" className="col-span-2 mt-1 bg-[#082D4A] text-white hover:bg-[#08223A]">
            <UserPlus size={15} className="mr-1.5" /> Create account
          </Button>
        </div>

        {/* Access requests */}
        {requests.length > 0 && (
          <div className="mt-4 rounded-xl border border-[#082D4A]/25 bg-[#082D4A]/5 p-3" data-testid="access-requests">
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[#082D4A]"><HeartHandshake size={15} /> Access requests {requests.some((r) => r.status === "pending") && <span className="rounded-full bg-[#D4A017] px-1.5 text-xs text-white">{requests.filter((r) => r.status === "pending").length}</span>}</div>
            <div className="space-y-2">
              {requests.map((r) => (
                <div key={r.id} data-testid={`request-row-${r.id}`} className="rounded-xl border border-[#D8E0E8] bg-white p-2 text-sm">
                  <div className="flex items-start gap-2">
                    <div className="flex-1">
                      <span className="font-medium">{r.name}</span>
                      {r.status !== "pending" && <span className={`ml-2 rounded-full px-2 py-0.5 text-xs capitalize ${r.status === "approved" ? "bg-[#E8EEF3] text-[#082D4A]" : "bg-red-50 text-red-500"}`}>{r.status}</span>}
                      {r.note && <p className="mt-0.5 text-xs text-[#64748B]">{r.note}</p>}
                    </div>
                    {r.status === "pending" ? (
                      <div className="flex gap-1.5">
                        <Button size="sm" onClick={() => approveRequest(r.id)} data-testid={`approve-request-${r.id}`} className="h-7 bg-[#082D4A] px-2 text-white hover:bg-[#08223A]"><Check size={14} className="mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" onClick={() => declineRequest(r.id)} data-testid={`decline-request-${r.id}`} className="h-7 border-[#D8E0E8] px-2"><X size={14} /></Button>
                      </div>
                    ) : (
                      <button onClick={() => removeRequest(r.id)} data-testid={`remove-request-${r.id}`} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Donations */}
        {donations.donations.length > 0 && (
          <div className="mt-4 rounded-xl border border-[#D4A017]/25 bg-[#D4A017]/5 p-3" data-testid="donations-panel">
            <div className="mb-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-[#D4A017]">
              <Heart size={15} className="fill-[#D4A017]" /> Donations
              {Object.entries(donations.totals).map(([cur, amt]) => (
                <span key={cur} className="rounded-full bg-white px-2 py-0.5 text-xs text-[#082D4A]">{amt} {cur}</span>
              ))}
            </div>
            <div className="max-h-40 space-y-1.5 overflow-y-auto">
              {donations.donations.map((d) => (
                <div key={d.id} data-testid={`donation-row-${d.id}`} className="flex items-center justify-between rounded-lg border border-[#D8E0E8] bg-white px-2.5 py-1.5 text-sm">
                  <span className="truncate">{d.donor_name || d.payer_name || "Anonymous"}{d.message ? <span className="ml-1 text-xs text-[#94A3B8]">— {d.message}</span> : null}</span>
                  <span className="shrink-0 font-semibold text-[#082D4A]">{d.amount} {d.currency} {d.status !== "completed" && <span className="ml-1 text-xs font-normal capitalize text-[#94A3B8]">({d.status})</span>}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Email preview */}
        <div className="mt-3 rounded-xl border border-[#D8E0E8] bg-white p-3" data-testid="email-preview-panel">
          <div className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-[#082D4A]"><MailCheck size={15} /> Preview emails</div>
          <p className="mb-2 text-xs text-[#64748B]">Send yourself a sample so you can see exactly what relatives receive.</p>
          <Input className="h-8 bg-white" data-testid="preview-email-input" value={previewEmail} onChange={(e) => setPreviewEmail(e.target.value)} placeholder="you@example.com" />
          <div className="mt-2 flex gap-2">
            <Button onClick={() => sendPreview("welcome")} disabled={previewBusy} data-testid="preview-welcome-btn" variant="outline" className="h-8 flex-1 border-[#082D4A] text-[#082D4A] hover:bg-[#E8EEF3]">Welcome sample</Button>
            <Button onClick={() => sendPreview("reset")} disabled={previewBusy} data-testid="preview-reset-btn" variant="outline" className="h-8 flex-1 border-[#082D4A] text-[#082D4A] hover:bg-[#E8EEF3]">Reset sample</Button>
          </div>
        </div>

        {/* Invite reminders */}
        <div className="mt-3 rounded-xl border border-[#D8E0E8] bg-white p-3" data-testid="reminder-settings-panel">
          <div className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-[#082D4A]"><BellRing size={15} /> Invite reminders</div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm text-[#475569]">Remind unaccepted invites after</span>
            <select data-testid="reminder-days-select" value={reminderCfg.invite_reminder_days} onChange={(e) => saveReminders({ invite_reminder_days: Number(e.target.value) })} className="rounded-md border border-[#D8E0E8] bg-white px-2 py-1 text-sm text-[#082D4A]">
              <option value={2}>2 days</option>
              <option value={3}>3 days</option>
              <option value={5}>5 days</option>
              <option value={7}>7 days</option>
            </select>
          </div>
          <label className="mt-2 flex cursor-pointer items-center justify-between">
            <span className="text-sm text-[#475569]">Send a second gentle nudge</span>
            <Switch checked={reminderCfg.invite_second_nudge} onCheckedChange={(v) => saveReminders({ invite_second_nudge: v })} data-testid="reminder-second-switch" />
          </label>
          {reminderCfg.invite_second_nudge && (
            <div className="mt-2 flex items-center justify-between gap-2">
              <span className="text-sm text-[#475569]">Second nudge after</span>
              <select data-testid="reminder-second-days-select" value={reminderCfg.invite_second_nudge_days} onChange={(e) => saveReminders({ invite_second_nudge_days: Number(e.target.value) })} className="rounded-md border border-[#D8E0E8] bg-white px-2 py-1 text-sm text-[#082D4A]">
                <option value={5}>5 days</option>
                <option value={7}>7 days</option>
                <option value={10}>10 days</option>
                <option value={14}>14 days</option>
              </select>
            </div>
          )}
        </div>

        {/* Invite links */}
        <div className="mt-4 rounded-xl border border-[#D4A017]/30 bg-[#D4A017]/5 p-3">
          <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-[#D4A017]"><Ticket size={15} /> Invite links</div>
          <p className="mb-2 text-xs text-[#64748B]">Generate a one-time link so a relative can set their own username & password. Expires after use or 7 days.</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">For (optional name)</Label><Input className="h-8 bg-white" data-testid="invite-name-input" value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="e.g. Aunt Rose" /></div>
            <div className="space-y-1">
              <Label className="text-xs">Role</Label>
              <Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}>
                <SelectTrigger className="h-8 bg-white" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="member">Member</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-2 space-y-1"><Label className="text-xs">Their email <span className="font-normal text-[#94A3B8]">(optional — we'll email the link now + a reminder in 3 days if unused)</span></Label><Input className="h-8 bg-white" data-testid="invite-email-input" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="relative@example.com" /></div>
          <Button onClick={generateInvite} disabled={busy} data-testid="generate-invite-button" className="mt-2 w-full bg-[#D4A017] text-white hover:bg-[#B0850F]">
            <Link2 size={15} className="mr-1.5" /> Generate invite link
          </Button>

          {invites.length > 0 && (
            <div className="mt-3 space-y-2" data-testid="invite-list">
              {invites.map((inv) => {
                const st = inviteStatus(inv);
                const active = !inv.used && !inv.expired;
                return (
                  <div key={inv.id} data-testid={`invite-row-${inv.id}`} className="rounded-xl border border-[#D8E0E8] bg-white p-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="flex-1 truncate">
                        <span className="font-medium">{inv.name || "Family member"}</span>
                        <span className="ml-1 text-[#64748B] capitalize">· {inv.role}</span>
                      </span>
                      <span className={`rounded-full px-2 py-0.5 text-xs ${st.cls}`}>{st.label}</span>
                      <button onClick={() => revoke(inv.id)} data-testid={`revoke-invite-${inv.id}`} className="text-red-500 hover:text-red-700" title="Delete invite"><Trash2 size={14} /></button>
                    </div>
                    {active && (
                      <div className="mt-1.5 flex items-center gap-1.5">
                        <Input readOnly value={inviteUrl(inv.token)} data-testid={`invite-url-${inv.id}`} className="h-7 flex-1 bg-[#F4F6F8] text-xs" onFocus={(e) => e.target.select()} />
                        <Button size="sm" variant="outline" onClick={() => copyLink(inv.token)} data-testid={`copy-invite-${inv.id}`} className="h-7 border-[#D8E0E8] px-2">
                          {copied === inv.token ? <Check size={14} className="text-[#082D4A]" /> : <Copy size={14} />}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Existing accounts */}
        <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.username}`} className="rounded-xl border border-[#D8E0E8] bg-white p-2 text-sm">
              <div className="flex items-center gap-2">
                {u.role === "admin" ? <Shield size={16} className="text-[#D4A017]" /> : <User size={16} className="text-[#64748B]" />}
                <div className="flex-1">
                  <span className="font-medium">{u.name || u.username}</span> <span className="text-[#64748B]">@{u.username}</span>
                  <span className="ml-2 rounded-full bg-[#E8EEF3] px-2 py-0.5 text-xs capitalize text-[#082D4A]">{u.role}</span>
                  {family?.owner_id === u.id && <span className="ml-1 rounded-full bg-[#D4A017]/15 px-2 py-0.5 text-xs font-medium text-[#B0850F]">Owner</span>}
                </div>
                {family?.is_owner && family?.owner_id !== u.id && (
                  <button onClick={() => toggleRole(u)} data-testid={`toggle-role-${u.username}`} title={u.role === "admin" ? "Remove admin" : "Make admin"} className="hover:opacity-70">
                    <Shield size={15} className={u.role === "admin" ? "text-[#D4A017]" : "text-[#94A3B8]"} />
                  </button>
                )}
                <button onClick={() => { setResetFor(resetFor === u.id ? null : u.id); setResetPw(""); }} data-testid={`reset-password-${u.username}`} className="text-[#082D4A] hover:text-[#08223A]" title="Reset password"><KeyRound size={15} /></button>
                {family?.owner_id !== u.id && (
                  <button onClick={() => remove(u.id)} data-testid={`delete-user-${u.username}`} className="text-red-500 hover:text-red-700"><Trash2 size={15} /></button>
                )}
              </div>
              {resetFor === u.id && (
                <div className="mt-2 flex items-center gap-1.5" data-testid={`reset-row-${u.username}`}>
                  <Input type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} data-testid={`reset-input-${u.username}`} placeholder="New password" className="h-7 flex-1 bg-[#F4F6F8] text-xs" autoFocus />
                  <Button size="sm" onClick={() => doReset(u.id)} data-testid={`reset-confirm-${u.username}`} className="h-7 bg-[#082D4A] px-2 text-white hover:bg-[#08223A]"><Check size={14} /></Button>
                  <Button size="sm" variant="outline" onClick={() => setResetFor(null)} className="h-7 border-[#D8E0E8] px-2"><X size={14} /></Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
